import React, { useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useConnectCustomIntegrationMutation } from '../../../store/user/user'
import { addToast } from '../../../store/toast/toastSlice'
import { readState } from '../../../common/integrations'
import { ROUTER_BASEPATH } from '../../../common/constants'
import { RedirectProgress } from '../../../components/RedirectProgress/RedirectProgress'
import { customIntegrationCallbackPath, customIntegrationDetailsPath } from '../constants'

export const CustomIntegrationCallbackView: React.FC = () => {
  const { customIntegrationId } = useParams<{ customIntegrationId: string }>() as { customIntegrationId: string }
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [connectCustomIntegration, { isSuccess, isError }] = useConnectCustomIntegrationMutation()
  const startedRef = useRef<boolean>(false)

  useEffect(() => {
    if (startedRef.current) {
      return
    }
    startedRef.current = true
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const error = searchParams.get('error')
    const startedHere = state !== null && readState(state) !== null
    if (error || !code || !state || !startedHere) {
      const description = searchParams.get('error_description')
      const fallback = startedHere ? 'The authorization did not return a code.' : 'The authorization was not started from this browser. Please connect again.'
      dispatch(
        addToast({
          variant: 'error',
          title: 'Failed to connect the MCP server',
          messages: [description ?? error ?? fallback],
          delay: 10000,
        }),
      )
      navigate(customIntegrationDetailsPath(customIntegrationId), { replace: true })
      return
    }
    connectCustomIntegration({
      accountId,
      customIntegrationId,
      payload: {
        connectType: 'state_oauth',
        code,
        state,
        redirectUri: window.location.origin + ROUTER_BASEPATH + customIntegrationCallbackPath(customIntegrationId),
      },
    })
  }, [accountId, customIntegrationId, connectCustomIntegration, dispatch, navigate, searchParams])

  useEffect(() => {
    if (!isSuccess) {
      return
    }
    const state = searchParams.get('state')
    const redirectUri = state ? readState(state)?.redirectUri : null
    const returnPath = redirectUri ? new URL(redirectUri).pathname.replace(ROUTER_BASEPATH, '') : customIntegrationDetailsPath(customIntegrationId)
    dispatch(addToast({ variant: 'success', title: 'MCP server connected.' }))
    navigate(returnPath, { replace: true })
  }, [isSuccess, searchParams, customIntegrationId, dispatch, navigate])

  useEffect(() => {
    if (isError) {
      navigate(customIntegrationDetailsPath(customIntegrationId), { replace: true })
    }
  }, [isError, navigate, customIntegrationId])

  return <RedirectProgress />
}
